import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgToastTranslocoService } from 'src/app/common/services/ng-toast-transloco/ng-toast-transloco.service';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { LoaderService } from 'src/app/common/services/loader/loader.service';
import { LoginDetailsService } from '../../services/login-details.service';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import moment from 'moment';

@Component({
  selector: 'ds-verify-details',
  templateUrl: './verify-details.component.html',
  styleUrl: './verify-details.component.scss'
})
export class VerifyDetailsComponent implements OnInit, AfterViewInit {

  @Output() goToSignin = new EventEmitter();
  @Output() verificationCompleted = new EventEmitter<{ accountNumber: string; dateOfBirth: string; zipCode: string }>();
  @Output() logout = new EventEmitter();

  @Input() accountNumber: string = '';

  verifyDetailsForm: FormGroup;
  maxDate: Date;
  dateTimeFormat: string;
  datePickerPlaceholder: string;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private toast: NgToastTranslocoService,
    private loginDetailsService: LoginDetailsService,
    private cdr: ChangeDetectorRef,
    private loaderService: LoaderService,
    private dateAdapter: DateAdapter<any>,
    @Inject(MAT_DATE_FORMATS) private dateFormats: any
  ) {
    this.initForm();
    this.maxDate = new Date();
    this.dateTimeFormat = this.loginDetailsService.supportedDateTimeFormat || 'MMM DD, YYYY';
    const momentFormat = this.configureDateFormat();
    this.datePickerPlaceholder = momentFormat.toLowerCase();
  }

  configureDateFormat(): string {
    // Update the date formats based on config
    // Convert Angular format to Moment format
    let inputFormat = this.dateTimeFormat;

    // Detect if format uses month name (MMM) - case insensitive
    const hasMonthName = /MMM/i.test(inputFormat);

    let momentFormat: string;

    if (hasMonthName) {
      if (inputFormat.includes('dd')) {
        // For month name formats: MMM dd,yyyy → MMM DD YYYY
        momentFormat = inputFormat
          .replace(/MMM/gi, 'MMM')    // Normalize to uppercase MMM
          .replace(/dd/gi, 'DD')      // day
          .replace(/yyyy/gi, 'YYYY')  // year
          .replace(/[,\/]/g, ' ')     // replace commas and slashes with spaces
          .replace(/\s+/g, ' ')       // normalize multiple spaces
          .trim();
      } else {
        // For month name formats: MMM d,yyyy → MMM D YYYY
        momentFormat = inputFormat
          .replace(/MMM/gi, 'MMM')    // Normalize to uppercase MMM
          .replace(/d/gi, 'D')        // day
          .replace(/yyyy/gi, 'YYYY')  // year
          .replace(/[,\/]/g, ' ')     // replace commas and slashes with spaces
          .replace(/\s+/g, ' ')       // normalize multiple spaces
          .trim();
      }
    } else {
      // For numeric formats: mm/dd/yyyy or dd,mm,yyyy → MM DD YYYY
      momentFormat = inputFormat
        .replace(/yyyy/gi, 'YYYY')  // year
        .replace(/dd/gi, 'DD')      // day
        .replace(/mm/gi, 'MM')      // month numeric
        .replace(/[,\/]/g, ' ')     // replace commas and slashes with spaces
        .replace(/\s+/g, ' ')       // normalize multiple spaces
        .trim();
    }

    this.dateFormats.display.dateInput = momentFormat;
    this.dateFormats.parse.dateInput = [momentFormat, 'MM/DD/YYYY', 'MMM DD YYYY', 'DD MM YYYY', 'DD/MM/YYYY'];

    return momentFormat;
  }

  ngOnInit(): void {
    this.initForm();
    if (this.accountNumber) {
      this.verifyDetailsForm.patchValue({ accountNumber: this.accountNumber });
    }
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  initForm() {
    this.verifyDetailsForm = this.fb.group({
      accountNumber: ['', [Validators.required]],
      dateOfBirth: [null, [Validators.required]],
      zipCode: ['', [Validators.required]]
    });
  }

  get accountNumberControl() {
    return this.verifyDetailsForm.get('accountNumber');
  }

  get dateOfBirth() {
    return this.verifyDetailsForm.get('dateOfBirth');
  }

  get zipCode() {
    return this.verifyDetailsForm.get('zipCode');
  }

  async confirm() {
    if (!this.verifyDetailsForm.valid) {
      this.verifyDetailsForm.markAllAsTouched();
      this.cdr.detectChanges();
      return;
    }

    const accountNumber = this.accountNumberControl?.value;
    const dateOfBirthValue = this.dateOfBirth?.value;
    const zipCode = this.zipCode?.value;

    // Format date to DD/MM/YYYY for backend
    let dateOfBirth = '';
    if (dateOfBirthValue) {
      dateOfBirth = moment(dateOfBirthValue).format('DD/MM/YYYY');
    }

    if (accountNumber && dateOfBirth && zipCode) {
      this.verificationCompleted.emit({ accountNumber, dateOfBirth, zipCode });
    }
  }

  goBack() {
    this.goToSignin.emit();
  }
}
